import {replaceSSO} from "@COMPONENTS/common/commonUtil";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import {EventPrice} from "@QUERIES/prdt/event/useEventPrice";
import {EventScheduleTime} from "@QUERIES/prdt/event/useEventScheduleTrip";
import {ReserveDetail} from "@QUERIES/prdt/reserve/useReserveDetail";
import dayjs from "dayjs";

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const getDayStayInfo = ({dayCnt, dayStayInfo}: {dayCnt: number; dayStayInfo: string}) => {
	/* 여행 기간 */
	if (!dayStayInfo) return null;
	return dayCnt > 0 && dayCnt < 10 ? dayStayInfo.replace("0박", "") : dayStayInfo;
};

const getTourPathInfo = ({tourPatInfo = null}: {tourPatInfo: string}) => {
	/* 방문 도시 */
	if (!tourPatInfo) return null;
	return tourPatInfo.replace(/-/g, " → ");
};

const getTrafficIcon = (event: EventDetail) => {
	const {trTp, trCompanyNm, seatClassCd} = event;
	const icons = {
		A: seatClassCd && seatClassCd !== "EC" ? "air-premium" : "air",
		T: "train",
		S: "ship",
		B: "bus",
	};

	return {
		icon: (icons[trTp] as string) ?? "transport-not",
		name: trTp ? trCompanyNm : "교통불포함",
	};
};
/**
 * divi "out" 출발항공, "in" 도착항공
 */
const getTrafficIconUrl = (
	{trTp, trCompanySnm, inAirCd}: {trTp: string; trCompanySnm: string; inAirCd?: string},
	divi: "out" | "in" = "out",
) => {
	const airCd = divi === "out" ? trCompanySnm : inAirCd;
	const STATIC_URL = publicRuntimeConfig.ENV_STATIC_CDN_URL;
	const src =
		{
			A: `${STATIC_URL}/images/air/${airCd}.png`,
			T: `${STATIC_URL}/images/air/TRAIN.png`,
			S: `${STATIC_URL}/images/air/SHIP.png`,
			B: `${STATIC_URL}/images/air/BUS.png`,
		}[trTp] || `${STATIC_URL}/images/air/ETC.png`;
	return src;
};

const departureStart = ({
	evStartDt,
	outDeprtTm,
	isMobile = false,
}: {
	evStartDt: string;
	outDeprtTm: string;
	isMobile?: boolean;
}) => {
	const startObj = dayjs(evStartDt);
	if (isMobile) {
		return `${startObj.format("MM.DD")} 
      (${daykor[startObj.day()]}) ${(outDeprtTm && `${outDeprtTm.substr(0, 2)}:${outDeprtTm.substr(2)}`) || ""}`;
	}
	return `${startObj.format("YYYY.MM.DD")} 
      (${daykor[startObj.day()]}) ${(outDeprtTm && `${outDeprtTm.substr(0, 2)}:${outDeprtTm.substr(2)}`) || ""}`;
};

const departureArrival = ({evStartDt, outArrvTm, outDayDiff}) => {
	const startObj = dayjs(evStartDt);
	return `${startObj.add(outDayDiff, "day").format("YYYY.MM.DD")} 
  (${daykor[startObj.add(outDayDiff, "day").day()]}) 
  ${(outArrvTm && `${outArrvTm.substr(0, 2)}:${outArrvTm.substr(2)}`) || ""}`;
};

const returnStart = ({evArriveDt, inDeprtTm, inDayDiff}) => {
	const arriveObj = dayjs(evArriveDt);
	return `${arriveObj.add(-inDayDiff, "day").format("YYYY.MM.DD")} 
  (${daykor[arriveObj.add(-inDayDiff, "day").day()]}) 
  ${(inDeprtTm && `${inDeprtTm.substr(0, 2)}:${inDeprtTm.substr(2)}`) || ""}`;
};

const returnArrival = ({
	evArriveDt,
	inArrvTm,
	isMobile = false,
}: {
	evArriveDt: string;
	inArrvTm: string;
	isMobile?: boolean;
}) => {
	const arriveObj = dayjs(evArriveDt);

	if (isMobile) {
		return `${arriveObj.format("MM.DD")} 
		(${daykor[arriveObj.day()]}) ${(inArrvTm && `${inArrvTm.substr(0, 2)}:${inArrvTm.substr(2)}`) || ""}`;
	}
	return `${arriveObj.format("YYYY.MM.DD")} 
    (${daykor[arriveObj.day()]}) ${(inArrvTm && `${inArrvTm.substr(0, 2)}:${inArrvTm.substr(2)}`) || ""}`;
};

const timeToDeparture = (props: EventDetail | ReserveDetail) => {
	const {outDeprtTm, outArrvTm} = props;
	if (!outDeprtTm || !outArrvTm) return "";

	const departTimeObj = dayjs(departureStart(props));
	const arrivalTimeObj = dayjs(departureArrival(props));
	const timeDiffMinutes = arrivalTimeObj.diff(departTimeObj, "minutes");

	const hours = Math.floor(timeDiffMinutes / 60);
	const minutes = timeDiffMinutes % 60;

	const formattedHours = hours.toString().padStart(2, "0");
	const formattedMinutes = minutes.toString().padStart(2, "0");

	return `총 ${formattedHours}시간 ${formattedMinutes}분`;
};
const timeToReturn = (props: EventDetail | ReserveDetail) => {
	const {inDeprtTm, inArrvTm} = props;
	if (!inDeprtTm || !inArrvTm) return "";

	const departTimeObj = dayjs(returnStart(props));
	const arrivalTimeObj = dayjs(returnArrival(props));
	const timeDiffMinutes = arrivalTimeObj.diff(departTimeObj, "minutes");

	const hours = Math.floor(timeDiffMinutes / 60);
	const minutes = timeDiffMinutes % 60;

	const formattedHours = hours.toString().padStart(2, "0");
	const formattedMinutes = minutes.toString().padStart(2, "0");

	return `총 ${formattedHours}시간 ${formattedMinutes}분`;
};

const flightLocation = (scheduleDetailTm: EventScheduleTime[], goodsAreaOne: string) => {
	const result = {outArrvCityNm: "", outDeprtCityNm: "", inArrvCityNm: "", inDeprtCityNm: ""};
	if (!scheduleDetailTm.length) return result;

	const {outArrvCityNm, outDeprtCityNm, inArrvCityNm, inDeprtCityNm, trTp} = scheduleDetailTm[0];
	// 상품코드 K (국내) 이면서 운송수단이 항공이 아닌경우
	if (goodsAreaOne === "K" && trTp !== "A") return result;
	return {
		outArrvCityNm,
		outDeprtCityNm,
		inArrvCityNm,
		inDeprtCityNm,
	};
};

/** 총 소요시간 ex) 총 hh시간 mi분 */
const scheduleTimeText = ({hh = "", mi = ""}) => {
	if (!hh || !mi) return "";
	return `총 ${hh ? `${hh}시간` : ``} ${mi ? `${mi}분` : ``}`;
};

const getPrice = (price: EventPrice) => {
	if (!price) return null;
	const {
		adtPrice,
		bafAdtCost,
		airTaxAdtCost,
		chdPrice,
		bafChdCost,
		airTaxChdCost,
		infPrice,
		bafInfCost,
		airTaxInfCost,
	} = price;
	const adult = Number(adtPrice + bafAdtCost + airTaxAdtCost);
	const child = Number(chdPrice + bafChdCost + airTaxChdCost);
	const infant = Number(infPrice + bafInfCost + airTaxInfCost);
	return {adult, child, infant};
};

/* 만 나이 구하기 */
const getAge = (yyyyMMdd = "", stdDt = "") => {
	const regExpStDt = /(\d{4})[.|-]*(\d{2})[.|-]*(\d{2})/.exec(stdDt);
	const regExpInpDt = /(\d{4})[.|-]*(\d{2})[.|-]*(\d{2})/.exec(yyyyMMdd);
	const startDt = regExpStDt
		? new Date(Number(regExpStDt[1]), Number(regExpStDt[2]), Number(regExpStDt[3]))
		: new Date();
	const inputDt = new Date(Number(regExpInpDt[1]), Number(regExpInpDt[2]), Number(regExpInpDt[3]));
	let years = startDt.getFullYear() - inputDt.getFullYear();
	inputDt.setFullYear(startDt.getFullYear());
	if (startDt < inputDt) years--;
	return years;
};

/** 허니문 에어텔 여부 */
const isGoodsTpFH = (goodsTp: string) => ["F", "H"].indexOf(goodsTp) > -1;

/* 예약 안내사항 분기 키 (goodsTp / fixRervCd 기준) */
type ReserveNoticeKey = "F" | "H" | "ND" | "YT" | "DEFAULT";

const getReserveNoticeKey = (goodsTp: string, fixRervCd: string | null): ReserveNoticeKey => {
	if (goodsTp === "F") return "F";
	if (goodsTp === "H") return "H";
	if (fixRervCd === "ND") return "ND";
	if (fixRervCd === "YT") return "YT";
	return "DEFAULT";
};

/* 예약 안내사항 문구 (모바일/데스크탑 공용) */
const RESERVE_NOTICE_TEXTS: Record<ReserveNoticeKey, string[]> = {
	// 안내사항_자유여행
	F: [
		"홈페이지를 통한 예약은 확정이 아닌 접수 상태이며, 담당자 확인 후 최종 요금 및 예약 확정이 진행됩니다.",
		"예약금은 예약 확정 후 담당자를 통해 안내되며, 지정 시간 내 예약금을 결제하지 않으신 경우 예약이 임의 취소될 수 있습니다.",
		"항공권 또는 항공권이 포함된 상품의 경우 세금 및 예상 유류할증료가 포함된 가격이며, 유류할증료는 유가 및 환율에 따라 변동될 수 있습니다.",
		"최저 항공료 좌석 마감 시 또는 여행하는 날짜의 현지 박람회 행사 등에 의해 추가요금이 발생할 수 있습니다.",
		"아동/유아 기준은 항공사마다 상이하여 예약 후 최종 확정됩니다.",
		"모든 상품의 호텔은 2인 1실 사용 기준의 1인 요금입니다. 1인 1실 사용 시 추가요금이 발생될 수 있고, 성수기 기간은 리조트별 서차지가 발생할 수 있습니다.",
	],
	// 안내사항_허니문
	H: [
		"본 상품은 허니문 전용 상품으로 비허니문일 경우 패널티가 부과될 수 있습니다. (지역별 상이)",
		"예약금은 예약 확정 후 담당자를 통해 안내되며, 지정 시간 내 예약금을 결제하지 않으신 경우 예약이 임의 취소될 수 있습니다.",
		"항공권 또는 항공권이 포함된 상품의 경우 세금 및 예상 유류할증료가 포함된 가격이며, 유류할증료는 유가 및 환율에 따라 변동될 수 있습니다.",
		"최저 항공료 좌석 마감 시 또는 여행하는 날짜의 현지 박람회 행사 등에 의해 추가요금이 발생할 수 있습니다.",
		"아동/유아 기준은 항공사마다 상이하여 예약 후 최종 확정됩니다.",
		"모든 상품의 호텔은 2인 1실 사용 기준의 1인 요금입니다. 1인 1실 사용 시 추가요금이 발생될 수 있고, 성수기 기간은 리조트별 서차지가 발생할 수 있습니다.",
		"일정표에 제공되는 프로그램은 고객 개인 사정으로 불참 시 환불되지 않습니다.",
	],
	// 안내사항_기본_가예약(예약금)
	ND: [
		"예약금 결제 시 '예약 접수' 상태로 진행되며, 담당자 확인 후 최종 확정됩니다.",
		"예약 확정 전까지 마이페이지, 고객센터를 통해 예약 취소 및 예약금 전액 환불이 가능합니다.",
		"확정 이후 취소 시 위약금이 발생할 수 있으니 행사별 약관을 반드시 확인하시기 바랍니다.",
		"항공권 포함 상품의 경우 세금 및 예상 유류할증료가 포함된 가격이며, 유가 및 환율에 따라 변동될 수 있습니다.",
		"아동·유아 요금은 항공사마다 상이하여 예약 확정 시 담당자가 안내드립니다.",
	],
	// 안내사항_기본_실예약(전액)
	YT: [
		"본 상품은 전액 결제 완료 시 여행 계약이 즉시 체결 및 예약 확정됩니다.",
		"항공권 포함 상품은 세금 및 유류할증료가 포함된 금액이며, 결제 완료 후 가격 변동 없이 확정됩니다.",
		"결제 미완료 시 좌석이 확보되지 않을 수 있으니 예약과 동시에 결제를 완료해 주세요.",
		"확정 이후 취소 시 위약금이 발생할 수 있으니 행사별 약관을 반드시 확인하시기 바랍니다.",
	],
	// 안내사항_기본_확정예약값 사용안함
	DEFAULT: [
		"홈페이지 예약은 '예약 접수' 상태로 진행되며, 담당자 확인 후 최종 요금 및 예약이 확정됩니다.",
		"예약 확정 전까지 마이페이지, 고객센터를 통해 예약 취소가 가능합니다.",
		"예약금은 확정 후 담당자를 통해 안내되며, 지정 시간 내 미결제 시 예약이 임의 취소될 수 있습니다.",
		"확정 이후 취소 시 위약금이 발생할 수 있으니 행사별 약관을 반드시 확인하시기 바랍니다.",
		"항공권 포함 상품의 경우 세금 및 예상 유류할증료가 포함된 가격이며, 유가 및 환율에 따라 변동될 수 있습니다.",
		"아동·유아 요금은 항공사마다 상이하여 예약 확정 시 담당자가 안내드립니다.",
	],
};

const getReserveNoticeTexts = (goodsTp: string, fixRervCd: string | null = null): string[] =>
	RESERVE_NOTICE_TEXTS[getReserveNoticeKey(goodsTp, fixRervCd)];

/* 예약하기버튼 */
const reserveButtonText = (
	event: EventDetail,
	type: "default" | "reserve" = "default",
): [type: "confirm" | "line" | "disabled" | "mainCtn" | "subCtn", text: string] => {
	if (!event) return ["disabled", ""];
	const today = dayjs();
	if (dayjs(event.evStartDt) <= today) return ["disabled", "출발완료"];
	if (event.evStaCd === "05") return ["disabled", "예약마감"];
	if (isGoodsTpFH(event.goodsTp)) return ["mainCtn", "예약접수"];
	if (!isGoodsTpFH(event.goodsTp) && event.evStaCd !== "05" && event.avlilBlockQty === 0)
		return ["mainCtn", "대기예약"];
	if (event.fixRervCd === "YT" && type === "reserve") return ["mainCtn", `전액 결제`];
	if (event.fixRervCd === "ND" && type === "reserve") return ["mainCtn", `예약금 결제`];
	return ["mainCtn", "예약하기"];
};

const reserveTypeText = (event: EventDetail): "예약접수" | "일반예약" | "대기예약" | "" => {
	if (!event) return "";
	if (isGoodsTpFH(event.goodsTp)) return "예약접수";
	if (!isGoodsTpFH(event.goodsTp) && event.evStaCd !== "05" && event.avlilBlockQty === 0) return "대기예약";
	return "일반예약";
};

type HrefReserveCompleteProps = {
	rervNo: string;
	fixRervCd: string;
	repreNm?: string;
	webCustNo?: string;
};
const hrefReserveComplete = async (props: HrefReserveCompleteProps) => {
	const {rervNo, fixRervCd, repreNm, webCustNo} = props;
	// fixRervCd: ND(가예약[예약금]) | YT(실예약[전액]) / null이면 DF(사용안함)
	const rervCd = fixRervCd ?? `DF`;
	const returnUrl =
		fixRervCd !== null
			? `${publicRuntimeConfig.ENV_TRAVEL_URL}/pkg/pay?rervNo=${rervNo}&rervCd=${rervCd}` // 결제 페이지 이동
			: `${publicRuntimeConfig.ENV_TRAVEL_URL}/pkg/reserve/complete?rervNo=${rervNo}&rervCd=${rervCd}`; // 완료 페이지 이동
	// 비회원 로그인 처리 (브릿지는 리다이렉트이므로 전체 페이지 POST submit)
	if (!webCustNo && repreNm && rervNo) {
		if (typeof document === "undefined") return;
		const form = document.createElement("form");
		form.method = "POST";
		form.action = `${publicRuntimeConfig.ENV_ACCOUNTS_API_DOMAIN}/bridge/nonuser-auth`;
		form.style.display = "none";
		const appendField = (name: string, value: string) => {
			const input = document.createElement("input");
			input.type = "hidden";
			input.name = name;
			input.value = value;
			form.appendChild(input);
		};
		appendField("rervNo", rervNo);
		appendField("custNm", repreNm);
		appendField("channelCode", "travel001");
		appendField("returnUrl", returnUrl);
		document.body.appendChild(form);
		form.submit();
		return;
	}
	return replaceSSO(returnUrl);
};

export {
	departureArrival,
	departureStart,
	flightLocation,
	getAge,
	getDayStayInfo,
	getPrice,
	getReserveNoticeTexts,
	getTourPathInfo,
	getTrafficIcon,
	getTrafficIconUrl,
	hrefReserveComplete,
	isGoodsTpFH,
	reserveButtonText,
	reserveTypeText,
	returnArrival,
	returnStart,
	scheduleTimeText,
	timeToDeparture,
	timeToReturn,
};
